"use client";

import { Calendar, User, ArrowLeft } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-12 bg-muted/40 animate-pulse rounded-md w-3/4" />
        <div className="h-6 bg-muted/30 animate-pulse rounded-md w-1/2" />
        <div className="h-96 bg-muted/30 animate-pulse rounded-md" />
        <div className="space-y-3">
          <div className="h-4 bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 bg-muted/30 animate-pulse rounded-md" />
          <div className="h-4 bg-muted/30 animate-pulse rounded-md w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">Unable to load post content.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <div className="mb-8">
        <Link to="/HomePage" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="size-4" />
          Back to home
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-primary text-primary-foreground rounded-md"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      <div className="flex flex-wrap items-center gap-6 pb-8 mb-10 border-b border-border">
        {post.author && (
          <div className="flex items-center gap-3">
            {post.author.avatar && post.author.avatar["48"] && (
              <img
                src={post.author.avatar["48"]}
                alt={post.author.name}
                className="size-10 rounded-lg"
                loading="lazy"
              />
            )}
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Author</span>
              <span className="text-sm font-default font-semibold text-foreground">{post.author.name}</span>
            </div>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-3">
            <Calendar className="size-5 text-primary" />
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Published</span>
              <span className="text-sm font-default font-semibold text-foreground">{formattedDate}</span>
            </div>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden border border-border shadow-md rounded-lg bg-card/40 backdrop-blur-sm">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose max-w-none font-default text-base md:text-lg leading-relaxed text-foreground/90 [&_h2]:font-default [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-foreground [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-foreground [&_p]:mb-6 [&_a]:text-primary [&_a]:underline hover:[&_a]:opacity-80 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_li]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:my-8 [&_img]:my-8 [&_img]:w-full"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-default font-semibold mr-2">
              Tags:
            </span>
            {post.tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="inline-block px-3 py-1 text-xs font-mono border border-border rounded-md bg-card/40 backdrop-blur-sm text-foreground/80"
              >
                #{tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {}
      {post.author && (
        <Card className="mt-12 bg-card/60 backdrop-blur-md border border-border shadow-md rounded-lg">
          <CardContent className="flex flex-col sm:flex-row gap-6 items-start sm:items-center p-6">
            {post.author.avatar && post.author.avatar["96"] && (
              <img
                src={post.author.avatar["96"]}
                alt={post.author.name}
                className="size-20 border-2 border-primary rounded-lg"
                loading="lazy"
              />
            )}
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-primary font-default font-semibold mb-1">
                Written by
              </div>
              <div className="font-default text-2xl font-bold text-foreground mb-2">
                {post.author.name}
              </div>
              <p className="text-sm font-default text-muted-foreground">
                Expert event coordinator at Paradise Cove, dedicated to crafting unforgettable wedding experiences.
              </p>
            </div>
            {post.author.link && (
              <Link to={post.author.link}>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-lg">
                  <User className="size-4" />
                  View Profile
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {}
      <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-center items-center pt-12 border-t border-border">
        <Link to="/HomePage">
          <Button variant="outline" className="border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary rounded-lg">
            <ArrowLeft className="size-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    </article>
  );
}

export default function Post() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="2621"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 relative overflow-hidden"
    >
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>
      </div>
    </section>
  );
}